/**
 * Transport envelopes: the TypeScript mirror of `socaity_schemas.transport`.
 * Describes the wire shape of Socaity/APIPod job responses plus the unified
 * job status enum used across the runtime.
 */

/** APIPod FileModel wire shape for media results ("FileResult" in media-toolkit). */
export interface FileModel {
  file_name: string;
  content_type: string;
  /** base64 payload or a downloadable URL. */
  content: string;
}

export interface JobLinks {
  status?: string | null;
  cancel?: string | null;
  stream?: string | null;
}

export interface JobMetrics {
  execution_time_s?: number | null;
  inference_time_s?: number | null;
  platform_queue_time_s?: number | null;
  provider_queue_time_s?: number | null;
  upload_time_s?: number | null;
}

/** Socaity/APIPod job envelope returned by submissions and GET /status. */
export interface SocaityJobResponse {
  job_id: string;
  status?: string | null;
  result?: any;
  error?: string | null;
  progress?: number | null;
  message?: string | null;
  service?: string | null;
  endpoint?: string | null;
  metrics?: JobMetrics | null;
  links?: JobLinks | null;
}

/** True when a parsed JSON payload is a Socaity/APIPod job envelope. */
export function isJobResponse(value: any): value is SocaityJobResponse {
  return !!value && typeof value === 'object' && typeof value.job_id === 'string';
}

/**
 * Sentinel returned by the response parser when the HTTP response is a live
 * stream (SSE or raw bytes). Carries the un-consumed fetch Response so a
 * StreamSession can be opened against it.
 */
export class StreamingResponse {
  constructor(public readonly response: Response) {}
}

// ---------------------------------------------------------------------------
// Unified job status (port of fastSDK's APIJobStatus)
// ---------------------------------------------------------------------------

export const JobStatus = {
  PENDING: 'PENDING',
  QUEUED: 'QUEUED',
  PROCESSING: 'PROCESSING',
  STREAMING: 'STREAMING',
  FINISHED: 'FINISHED',
  FAILED: 'FAILED',
  TIMEOUT: 'TIMEOUT',
  CANCELLED: 'CANCELLED',
  UNKNOWN: 'UNKNOWN',
} as const;

export type JobStatus = (typeof JobStatus)[keyof typeof JobStatus];

const STATUS_ALIASES: Record<string, JobStatus> = {
  CREATED: JobStatus.QUEUED,
  IN_QUEUE: JobStatus.QUEUED,
  STARTING: JobStatus.QUEUED,
  IN_PROGRESS: JobStatus.PROCESSING,
  RUNNING: JobStatus.PROCESSING,
  BOOTING: JobStatus.PROCESSING,
  COMPLETED: JobStatus.FINISHED,
  SUCCEEDED: JobStatus.FINISHED,
  CANCELED: JobStatus.CANCELLED,
  ABORTED: JobStatus.CANCELLED,
  TIMED_OUT: JobStatus.TIMEOUT,
  ERROR: JobStatus.FAILED,
  NOT_FOUND: JobStatus.FAILED,
};

/** Map any provider status string to the unified enum. */
export function jobStatusFromString(status: string | null | undefined): JobStatus {
  if (!status) return JobStatus.UNKNOWN;
  const upper = status.toUpperCase();
  if (upper in JobStatus) return upper as JobStatus;
  return STATUS_ALIASES[upper] ?? JobStatus.UNKNOWN;
}

export function isTerminalStatus(status: JobStatus): boolean {
  return (
    status === JobStatus.FINISHED
    || status === JobStatus.FAILED
    || status === JobStatus.TIMEOUT
    || status === JobStatus.CANCELLED
  );
}
